import { type ChatMessage } from "@vinyl/shared";
import { el } from "./dom.js";

const HELP_LINES = [
  "Commands: /g <message> for global, /who for online players, /help for this list.",
  "Party: /invite <name>, /leave. Plain text → local chat (12-tile radius).",
];

const MAX_HISTORY = 60;

export interface ChatPanelCallbacks {
  onSendChat: (channel: "global" | "local", text: string) => void;
  onWho: () => void;
  // M5.3 — party slash commands. Resolved client-side so the chat pipe
  // never carries them, and so zod validates the typed protocol payload.
  onPartyInvite: (targetCharacterName: string) => void;
  onPartyLeave: () => void;
  setInputBlocked: (blocked: boolean) => void;
}

export class ChatPanel {
  readonly root: HTMLElement;
  private readonly messages: HTMLElement;
  private readonly inputRow: HTMLElement;
  private readonly input: HTMLInputElement;
  private localCounter = 0;

  constructor(private readonly callbacks: ChatPanelCallbacks) {
    this.root = el("div", { class: "chat-panel" });
    this.messages = el("div", { class: "chat-messages" });
    this.input = el("input", {
      class: "chat-input",
      type: "text",
      maxlength: "500",
      placeholder: "Press Enter to chat — /g, /who, /help",
      autocomplete: "off",
    }) as HTMLInputElement;
    this.inputRow = el("div", { class: "chat-input-row" }, this.input);
    this.root.append(this.messages, this.inputRow);

    this.input.addEventListener("keydown", this.handleInputKey);
    window.addEventListener("keydown", this.handleGlobalKey);
  }

  appendServer(msg: ChatMessage): void {
    this.appendRow(msg.channel, msg.senderName, msg.text);
  }

  appendLocalSystem(text: string): void {
    this.localCounter++;
    this.appendRow("system", "System", text);
  }

  destroy(): void {
    window.removeEventListener("keydown", this.handleGlobalKey);
    this.input.removeEventListener("keydown", this.handleInputKey);
  }

  private appendRow(channel: ChatMessage["channel"], sender: string, text: string): void {
    const row = el("div", { class: `chat-message chat-${channel}` });
    if (channel === "system") {
      row.append(document.createTextNode(text));
    } else {
      const prefix = channel === "global" ? "[G] " : "";
      row.append(
        document.createTextNode(prefix),
        el("span", { class: "chat-sender" }, sender),
        document.createTextNode(": " + text),
      );
    }
    this.messages.append(row);
    this.messages.scrollTop = this.messages.scrollHeight;
    while (this.messages.childNodes.length > MAX_HISTORY) {
      this.messages.removeChild(this.messages.firstChild!);
    }
  }

  private isOpen(): boolean {
    return this.inputRow.classList.contains("visible");
  }

  private openInput(): void {
    if (this.isOpen()) return;
    this.inputRow.classList.add("visible");
    this.input.focus();
    this.callbacks.setInputBlocked(true);
  }

  private closeInput(): void {
    this.inputRow.classList.remove("visible");
    this.input.value = "";
    this.input.blur();
    this.callbacks.setInputBlocked(false);
  }

  private submit(): void {
    const raw = this.input.value;
    this.closeInput();
    const trimmed = raw.trim();
    if (trimmed.length === 0) return;

    if (trimmed.startsWith("/g ")) {
      const text = trimmed.slice(3).trim();
      if (text) this.callbacks.onSendChat("global", text);
      return;
    }
    if (trimmed === "/g") return;
    if (trimmed === "/who") {
      this.callbacks.onWho();
      return;
    }
    if (trimmed === "/help") {
      for (const line of HELP_LINES) this.appendLocalSystem(line);
      return;
    }
    // M5.3 — party commands. /invite needs a name; /leave is bare.
    if (trimmed.startsWith("/invite ")) {
      const name = trimmed.slice("/invite ".length).trim();
      if (!name) {
        this.appendLocalSystem("Usage: /invite <name>");
        return;
      }
      this.callbacks.onPartyInvite(name);
      return;
    }
    if (trimmed === "/invite") {
      this.appendLocalSystem("Usage: /invite <name>");
      return;
    }
    if (trimmed === "/leave") {
      this.callbacks.onPartyLeave();
      return;
    }
    if (trimmed.startsWith("/")) {
      this.appendLocalSystem(`Unknown command: ${trimmed}`);
      return;
    }
    this.callbacks.onSendChat("local", trimmed);
  }

  private readonly handleInputKey = (e: KeyboardEvent): void => {
    if (e.key === "Enter") {
      e.preventDefault();
      e.stopPropagation();
      this.submit();
    } else if (e.key === "Escape") {
      e.preventDefault();
      e.stopPropagation();
      this.closeInput();
    }
  };

  private readonly handleGlobalKey = (e: KeyboardEvent): void => {
    if (e.key !== "Enter") return;
    if (this.isOpen()) return;
    const active = document.activeElement;
    if (active instanceof HTMLInputElement || active instanceof HTMLTextAreaElement) return;
    e.preventDefault();
    this.openInput();
  };
}
